import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, Package, ShoppingBag, TrendingUp, Users, Plus, Pencil, Trash2, Upload, Gift, Tag, MapPin, Ticket, Mail } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../lib/auth";

const STATUS_OPTIONS = ["pending", "confirmed", "out_for_delivery", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  out_for_delivery: "bg-purple-100 text-purple-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};

// slugify helper for category slug generation
const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addons, setAddons] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [mlgSettings, setMlgSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");

  // dialog states
  const [openProd, setOpenProd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openAddon, setOpenAddon] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [openCat, setOpenCat] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [openCoupon, setOpenCoupon] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [openPin, setOpenPin] = useState(false);
  const [editingPin, setEditingPin] = useState(null);

  useEffect(() => {
    if (!user) nav("/login?next=/admin");
    else if (!isAdmin) nav("/");
  }, [user, isAdmin, nav]);

  const refresh = async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    try {
      const [s, o, p, c, a, cp, pn, en, mlg] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders"),
        api.get("/products"),
        api.get("/categories"),
        api.get("/addons"),
        api.get("/admin/coupons"),
        api.get("/admin/pincodes"),
        api.get("/admin/enquiries"),
        api.get("/admin/settings/mylocalgo"),
      ]);
      setStats(s.data);
      setOrders(o.data);
      setProducts(p.data);
      setCategories(c.data);
      setAddons(a.data);
      setCoupons(cp.data);
      setPincodes(pn.data);
      setEnquiries(en.data);
      setMlgSettings(mlg.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) refresh({ initial: true });
  }, [isAdmin]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success("Order status updated");
      refresh();
    } catch {
      toast.error("Failed to update status");
    }
  };

  // ---------------- Product editor ----------------
  const openNew = () => {
    setEditing({
      name: "",
      category: categories[0]?.slug || "birthday",
      price: 0,
      short_description: "",
      description: "",
      image_url: "",
      image_base64: null,
      featured: false,
      in_stock: true,
      product_type: "hamper",
      min_order_quantity: 1,
      toys: [],
    });
    setOpenProd(true);
  };
  const openEdit = (p) => { setEditing({ ...p, toys: p.toys || [] }); setOpenProd(true); };

  const saveProduct = async () => {
    if (!editing.name || !editing.category || editing.price < 0) return toast.error("Fill required fields");
    // Compute total base64 payload size across cover + all toys. MongoDB caps documents at 16 MB,
    // and base64 inflates by ~33%, so we cap at ~12 MB effective raw bytes.
    // (12 MB × 4/3 ≈ 16 MB base64 which just fits.)
    const b64Len = (s) => (typeof s === "string" && s.startsWith("data:") ? s.length : 0);
    const coverB64 = b64Len(editing.image_base64);
    const toysB64 = (editing.toys || []).reduce((sum, t) => sum + b64Len(t.image_base64), 0);
    const totalB64 = coverB64 + toysB64;
    const HARD_CAP = 12 * 1024 * 1024; // ~16 MB after base64 = MongoDB doc limit
    if (totalB64 > HARD_CAP) {
      const totalMB = (totalB64 / 1024 / 1024).toFixed(1);
      return toast.error(
        `Total uploaded images are ${totalMB} MB (over 12 MB combined limit). ` +
        "Use image URLs for some toys, or reduce image sizes. " +
        "MongoDB restricts each product to a single 16 MB document."
      );
    }
    try {
      const payload = { ...editing };
      if (payload.id) {
        await api.put(`/products/${payload.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      setOpenProd(false);
      setEditing(null);
      refresh();
    } catch (e) {
      // Common backend / infra errors: 413 (payload too large from ingress), 400 (validation)
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 413) {
        toast.error("Upload too large — please reduce image sizes and try again.");
      } else {
        toast.error(detail || "Failed to save product");
      }
    }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const handleImage = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("Image must be under 15MB");
    const uploaded = await uploadImageToStorage(file);
    if (uploaded && uploaded.url) {
      setter((prev) => ({ ...prev, image_url: uploaded.url, image_base64: null }));
    } else if (!uploaded) {
      // Legacy base64 fallback (only when object storage returned 503)
      const reader = new FileReader();
      reader.onload = () => setter((prev) => ({ ...prev, image_base64: reader.result, image_url: "" }));
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  // Upload to Emergent object storage. Returns {url,path} on success, null on infra-missing
  // (in which case the caller falls back to base64). Throws on other errors.
  const uploadImageToStorage = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded");
      return data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        toast.message("Using inline storage (object storage not configured).");
        if (file.size > 10 * 1024 * 1024) {
          toast.error("Image must be under 10MB for inline storage");
          return { fatal: true };
        }
        return null;
      }
      toast.error(err?.response?.data?.detail || "Upload failed");
      return { fatal: true };
    }
  };

  // ---------------- Add-on editor ----------------
  const openNewAddon = () => {
    setEditingAddon({
      name: "",
      description: "",
      price: 0,
      image_url: "",
      image_base64: null,
      in_stock: true,
    });
    setOpenAddon(true);
  };
  const openEditAddon = (a) => { setEditingAddon({ ...a }); setOpenAddon(true); };

  const saveAddon = async () => {
    if (!editingAddon.name || editingAddon.price < 0) return toast.error("Fill required fields");
    try {
      const payload = { ...editingAddon };
      if (payload.id) {
        await api.put(`/addons/${payload.id}`, payload);
        toast.success("Gift updated");
      } else {
        await api.post("/addons", payload);
        toast.success("Gift added");
      }
      setOpenAddon(false);
      setEditingAddon(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };
  const deleteAddon = async (id) => {
    if (!window.confirm("Delete this gift?")) return;
    await api.delete(`/addons/${id}`);
    toast.success("Deleted");
    refresh();
  };

  // ---------------- Category editor ----------------
  const openNewCat = () => {
    setEditingCat({ slug: "", name: "", description: "", parent_slug: null, order: 0, _isNew: true });
    setOpenCat(true);
  };
  const openEditCat = (c) => { setEditingCat({ ...c, _isNew: false }); setOpenCat(true); };

  const saveCat = async () => {
    if (!editingCat.name.trim()) return toast.error("Name is required");
    try {
      if (editingCat._isNew) {
        const slug = editingCat.slug.trim() || slugify(editingCat.name);
        await api.post("/categories", {
          slug,
          name: editingCat.name.trim(),
          description: editingCat.description,
          parent_slug: editingCat.parent_slug || null,
          order: editingCat.order || 0,
        });
        toast.success("Category added");
      } else {
        await api.put(`/categories/${editingCat.slug}`, {
          name: editingCat.name.trim(),
          description: editingCat.description,
          parent_slug: editingCat.parent_slug || null,
          order: editingCat.order || 0,
        });
        toast.success("Category updated");
      }
      setOpenCat(false);
      setEditingCat(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };
  const deleteCat = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"?`)) return;
    try {
      await api.delete(`/categories/${slug}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  // ---------------- Coupon editor ----------------
  const openNewCoupon = () => {
    setEditingCoupon({
      code: "",
      discount_type: "percent",
      discount_value: 10,
      min_order: 0,
      valid_until: "",
      usage_limit: 0,
      source: "manual",
      is_active: true,
      notes: "",
      _isNew: true,
    });
    setOpenCoupon(true);
  };
  const openEditCoupon = (c) => { setEditingCoupon({ ...c, _isNew: false }); setOpenCoupon(true); };
  const saveCoupon = async () => {
    if (!editingCoupon.code.trim() || editingCoupon.discount_value <= 0) return toast.error("Fill code and discount");
    try {
      const payload = { ...editingCoupon };
      const isNew = payload._isNew;
      delete payload._isNew;
      if (isNew) {
        await api.post("/coupons", payload);
        toast.success("Coupon added");
      } else {
        const { code, ...rest } = payload;
        await api.put(`/coupons/${code}`, rest);
        toast.success("Coupon updated");
      }
      setOpenCoupon(false);
      setEditingCoupon(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };
  const deleteCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    await api.delete(`/coupons/${code}`);
    toast.success("Deleted");
    refresh();
  };

  const toggleMlgOffer = async (nextActive) => {
    try {
      const { data } = await api.put("/admin/settings/mylocalgo", { is_active: nextActive });
      setMlgSettings(data);
      toast.success(nextActive ? "MyLocalGo offer activated" : "MyLocalGo offer paused");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update MyLocalGo offer");
    }
  };

  // ---------------- Pincode editor ----------------
  const openNewPin = () => {
    setEditingPin({ pincode: "", city: "", notes: "", is_active: true, _isNew: true });
    setOpenPin(true);
  };
  const openEditPin = (p) => { setEditingPin({ ...p, _isNew: false }); setOpenPin(true); };
  const savePin = async () => {
    if (!editingPin.pincode.trim()) return toast.error("Enter a pincode");
    try {
      const payload = { ...editingPin };
      const isNew = payload._isNew;
      delete payload._isNew;
      if (isNew) {
        await api.post("/pincodes", payload);
        toast.success("Pincode added");
      } else {
        await api.put(`/pincodes/${payload.pincode}`, payload);
        toast.success("Pincode updated");
      }
      setOpenPin(false);
      setEditingPin(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };
  const deletePin = async (pin) => {
    if (!window.confirm(`Delete pincode ${pin}?`)) return;
    await api.delete(`/pincodes/${pin}`);
    toast.success("Deleted");
    refresh();
  };

  // ---------------- Enquiries ----------------
  const ENQ_STATUS = ["new", "contacted", "quoted", "converted", "dropped"];
  const setEnquiryStatus = async (id, status) => {
    try {
      await api.put(`/admin/enquiries/${id}/status`, { status });
      toast.success("Status updated");
      refresh();
    } catch {
      toast.error("Failed to update");
    }
  };
  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const topCats = categories.filter((c) => !c.parent_slug);

  if (!isAdmin) return null;

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-10" data-testid="admin-page">
        <p className="label-tiny mb-3">Admin</p>
        <h1 className="font-serif text-4xl text-[#2a2928] sm:text-5xl">Dashboard</h1>

        {loading || !stats ? (
          <div className="mt-10 flex items-center gap-2 text-[#7a7571]">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
              <StatCard icon={ShoppingBag} label="Orders" value={stats.total_orders} testid="stat-orders" />
              <StatCard icon={TrendingUp} label="Revenue" value={formatINR(stats.total_revenue)} testid="stat-revenue" />
              <StatCard icon={Package} label="Products" value={stats.total_products} testid="stat-products" />
              <StatCard icon={Users} label="Customers" value={stats.total_customers} testid="stat-customers" />
            </div>

            <div className="mt-4 flex items-center justify-end">
              <button
                data-testid="admin-reset-demo-data"
                onClick={async () => {
                  const confirmed = window.confirm(
                    "This will PERMANENTLY delete ALL orders, enquiries and non-admin customers. " +
                    "Products, categories, coupons and pincodes will be kept. Continue?"
                  );
                  if (!confirmed) return;
                  const phrase = window.prompt('To confirm, type RESET (uppercase):');
                  if (phrase !== "RESET") { toast.message("Reset cancelled."); return; }
                  try {
                    const { data } = await api.post("/admin/reset-demo-data", { confirm: "RESET" });
                    toast.success(
                      `Deleted ${data.orders_deleted} orders, ${data.enquiries_deleted} enquiries, ${data.customers_deleted} customers.`
                    );
                    refresh();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Reset failed");
                  }
                }}
                className="rounded-full border border-red-200 bg-white px-4 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                Reset test data (orders, enquiries, customers)
              </button>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-10">
              <TabsList data-testid="admin-tabs">
                <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
                <TabsTrigger value="products" data-testid="admin-tab-products">Hampers</TabsTrigger>
                <TabsTrigger value="addons" data-testid="admin-tab-addons">Gifts</TabsTrigger>
                <TabsTrigger value="categories" data-testid="admin-tab-categories">Categories</TabsTrigger>
                <TabsTrigger value="coupons" data-testid="admin-tab-coupons">Coupons</TabsTrigger>
                <TabsTrigger value="pincodes" data-testid="admin-tab-pincodes">Pincodes</TabsTrigger>
                <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">
                  Enquiries
                  {enquiries.filter((e) => e.status === "new").length > 0 && (
                    <span className="ml-2 rounded-full bg-[#d08c8c] px-2 py-0.5 text-[10px] text-white">
                      {enquiries.filter((e) => e.status === "new").length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="mt-6">
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  {orders.length === 0 ? (
                    <div className="py-12 text-center text-[#7a7571]" data-testid="admin-no-orders">No orders yet.</div>
                  ) : (
                    <div className="space-y-3" data-testid="admin-orders-list">
                      {orders.map((o) => (
                        <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-2xl border border-[#e8e2da] p-4">
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                            <div className="md:col-span-2">
                              <div className="font-serif text-lg">{o.order_number}</div>
                              <div className="text-xs text-[#7a7571]">
                                {format(new Date(o.created_at), "PPP p")}
                              </div>
                              <div className="text-xs text-[#7a7571]">{o.customer_email}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Recipient</div>
                              <div className="text-sm">{o.recipient_name}</div>
                              <div className="text-xs text-[#7a7571]">{o.recipient_phone}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Delivery</div>
                              <div className="text-sm">{format(new Date(o.delivery_date), "PPP")}</div>
                              <div className="text-xs text-[#7a7571]">{o.delivery_slot}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Total</div>
                              <div className="font-serif text-lg">{formatINR(o.total)}</div>
                              <div className="text-xs text-[#7a7571]">{o.payment_mode.toUpperCase()} · {o.payment_status}</div>
                              {o.coupon_code ? (
                                <div
                                  data-testid={`admin-order-coupon-${o.id}`}
                                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#f4ede6] px-2 py-0.5 text-[11px] text-[#8a5a5a]"
                                  title={`Discount ${formatINR(o.discount_amount || 0)}`}
                                >
                                  <Ticket className="h-3 w-3" />
                                  <span className="font-mono">{o.coupon_code}</span>
                                  {o.discount_amount ? (
                                    <span className="text-[#b87a7a]">−{formatINR(o.discount_amount)}</span>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            <div className="flex flex-col items-start gap-2">
                              <span className={`rounded-full px-3 py-1 text-xs font-medium ${STATUS_COLORS[o.status]}`}>
                                {o.status.replace(/_/g, " ")}
                              </span>
                              <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                <SelectTrigger className="h-8 w-full rounded-full text-xs" data-testid={`admin-order-status-${o.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {STATUS_OPTIONS.map((s) => (
                                    <SelectItem value={s} key={s}>{s.replace(/_/g, " ")}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Full delivery details for the ops team */}
                          <div
                            data-testid={`admin-order-details-${o.id}`}
                            className="mt-4 grid grid-cols-1 gap-4 rounded-xl bg-[#faf7f3] p-4 md:grid-cols-3"
                          >
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Delivery address</div>
                              <div
                                data-testid={`admin-order-address-${o.id}`}
                                className="mt-1 whitespace-pre-line text-sm text-[#2a2928]"
                              >
                                {o.delivery_address || "—"}
                              </div>
                              <div className="mt-1 text-xs text-[#7a7571]">
                                {[o.city, o.pincode].filter(Boolean).join(" · ") || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Contact</div>
                              <div className="mt-1 text-sm text-[#2a2928]">{o.recipient_name || "—"}</div>
                              <div className="text-xs text-[#7a7571]">{o.recipient_phone || "—"}</div>
                              <div className="text-xs text-[#7a7571]">{o.customer_email}</div>
                            </div>
                            <div>
                              <div className="text-xs uppercase tracking-wider text-[#7a7571]">Message on card</div>
                              <div
                                data-testid={`admin-order-message-${o.id}`}
                                className="mt-1 whitespace-pre-line text-sm italic text-[#2a2928]"
                              >
                                {o.message_on_card ? `“${o.message_on_card}”` : "—"}
                              </div>
                            </div>
                          </div>

                          {/* Items with toys + addons for packing */}
                          {(o.items || []).length > 0 && (
                            <div
                              data-testid={`admin-order-items-${o.id}`}
                              className="mt-3 divide-y divide-[#e8e2da] rounded-xl border border-[#e8e2da]"
                            >
                              {o.items.map((it, idx) => (
                                <div key={`${o.id}-item-${idx}`} className="grid grid-cols-1 gap-2 p-3 md:grid-cols-4">
                                  <div className="md:col-span-2">
                                    <div className="text-sm font-medium text-[#2a2928]">
                                      {it.name} <span className="text-[#7a7571]">× {it.quantity}</span>
                                    </div>
                                    <div className="text-xs text-[#7a7571]">{formatINR(it.line_total)}</div>
                                  </div>
                                  <div>
                                    <div className="text-xs uppercase tracking-wider text-[#7a7571]">Toy in balloon</div>
                                    <div className="text-sm text-[#2a2928]">
                                      {(it.toys || []).length > 0
                                        ? it.toys.map((t) => t.name).join(", ")
                                        : it.product_type === "hamper" ? "—" : ""}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-xs uppercase tracking-wider text-[#7a7571]">Add-on gifts</div>
                                    <div className="text-sm text-[#2a2928]">
                                      {(it.addons || []).length > 0
                                        ? it.addons.map((a) => `${a.name} × ${a.quantity}`).join(", ")
                                        : "—"}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="products" className="mt-6">
                <div className="mb-4 flex justify-end">
                  <Button data-testid="admin-add-product-btn" onClick={openNew} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
                    <Plus className="mr-2 h-4 w-4" /> Add hamper
                  </Button>
                </div>
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="admin-products-list">
                    {products.map((p) => (
                      <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex gap-3 rounded-2xl border border-[#e8e2da] p-3">
                        <img src={p.image_base64 || p.image_url} alt={p.name} className="h-24 w-24 rounded-xl object-cover" />
                        <div className="flex flex-1 flex-col">
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="font-serif text-lg">{p.name}</div>
                            {p.featured && (
                              <span
                                data-testid={`admin-product-featured-${p.id}`}
                                className="rounded-full bg-[#fdf3f3] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#8a4a4a]"
                              >
                                Featured
                              </span>
                            )}
                            {!p.in_stock && (
                              <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-stone-500">
                                Hidden
                              </span>
                            )}
                          </div>
                          <div className="text-xs uppercase tracking-wider text-[#7a7571]">{p.category}</div>
                          <div className="mt-auto flex items-center justify-between">
                            <span className="font-serif">{formatINR(p.price)}</span>
                            <div className="flex gap-1">
                              <button data-testid={`admin-edit-product-${p.id}`} onClick={() => openEdit(p)} className="rounded-full p-2 hover:bg-[#f4ede6]">
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button data-testid={`admin-delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="rounded-full p-2 hover:bg-red-50">
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* GIFTS (Add-ons) */}
              <TabsContent value="addons" className="mt-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#7a7571]">
                    <Gift className="h-4 w-4 text-[#d08c8c]" />
                    These gifts appear on every hamper&apos;s page — shoppers add them to the box beneath the balloon.
                  </div>
                  <Button data-testid="admin-add-addon-btn" onClick={openNewAddon} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
                    <Plus className="mr-2 h-4 w-4" /> Add gift
                  </Button>
                </div>
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  {addons.length === 0 ? (
                    <div className="py-12 text-center text-[#7a7571]" data-testid="admin-no-addons">No gifts yet. Add chocolates, teddy bears, cards & more.</div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="admin-addons-list">
                      {addons.map((a) => (
                        <div key={a.id} data-testid={`admin-addon-${a.id}`} className="flex gap-3 rounded-2xl border border-[#e8e2da] p-3">
                          <img src={a.image_base64 || a.image_url} alt={a.name} className="h-24 w-24 rounded-xl object-cover" />
                          <div className="flex flex-1 flex-col">
                            <div className="font-serif text-lg">{a.name}</div>
                            <div className="line-clamp-2 text-xs text-[#7a7571]">{a.description}</div>
                            <div className="mt-auto flex items-center justify-between">
                              <span className="font-serif">{formatINR(a.price)}</span>
                              <div className="flex items-center gap-1">
                                {!a.in_stock && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-700">out</span>}
                                <button data-testid={`admin-edit-addon-${a.id}`} onClick={() => openEditAddon(a)} className="rounded-full p-2 hover:bg-[#f4ede6]">
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button data-testid={`admin-delete-addon-${a.id}`} onClick={() => deleteAddon(a.id)} className="rounded-full p-2 hover:bg-red-50">
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* CATEGORIES */}
              <TabsContent value="categories" className="mt-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#7a7571]">
                    <Tag className="h-4 w-4 text-[#d08c8c]" />
                    Top-level categories appear as tabs on the shop. Sub-categories appear as refine chips.
                  </div>
                  <Button data-testid="admin-add-cat-btn" onClick={openNewCat} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
                    <Plus className="mr-2 h-4 w-4" /> Add category
                  </Button>
                </div>
                <div className="space-y-4" data-testid="admin-categories-list">
                  {topCats.map((parent) => {
                    const children = categories.filter((c) => c.parent_slug === parent.slug);
                    return (
                      <div key={parent.slug} className="rounded-3xl border border-[#e8e2da] bg-white p-5" data-testid={`admin-cat-${parent.slug}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="font-serif text-xl text-[#2a2928]">{parent.name}</div>
                            <div className="text-xs text-[#7a7571]">/{parent.slug}</div>
                            {parent.description && <div className="mt-1 text-sm text-[#7a7571]">{parent.description}</div>}
                          </div>
                          <div className="flex gap-1">
                            <button data-testid={`admin-edit-cat-${parent.slug}`} onClick={() => openEditCat(parent)} className="rounded-full p-2 hover:bg-[#f4ede6]">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button data-testid={`admin-delete-cat-${parent.slug}`} onClick={() => deleteCat(parent.slug)} className="rounded-full p-2 hover:bg-red-50">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        {children.length > 0 && (
                          <div className="mt-4 space-y-2 border-t border-[#e8e2da] pt-4">
                            {children.map((c) => (
                              <div key={c.slug} data-testid={`admin-subcat-${c.slug}`} className="flex items-center justify-between rounded-xl bg-[#faf9f6] px-4 py-2">
                                <div>
                                  <span className="text-sm font-medium">{c.name}</span>
                                  <span className="ml-2 text-xs text-[#7a7571]">/{c.slug}</span>
                                </div>
                                <div className="flex gap-1">
                                  <button data-testid={`admin-edit-cat-${c.slug}`} onClick={() => openEditCat(c)} className="rounded-full p-1.5 hover:bg-white">
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button data-testid={`admin-delete-cat-${c.slug}`} onClick={() => deleteCat(c.slug)} className="rounded-full p-1.5 hover:bg-white">
                                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              {/* COUPONS */}
              <TabsContent value="coupons" className="mt-6">
                {/* MyLocalGo partner offer toggle card */}
                <div
                  data-testid="admin-mlg-offer-card"
                  className="mb-6 rounded-3xl border border-[#e8e2da] bg-gradient-to-br from-[#fdf7f2] to-white p-6"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-start gap-3">
                      <div className="rounded-2xl bg-[#d08c8c]/10 p-3">
                        <Ticket className="h-5 w-5 text-[#d08c8c]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="font-serif text-xl text-[#2a2928]">MyLocalGo partner offer</div>
                          <span
                            data-testid="admin-mlg-status-badge"
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              mlgSettings?.is_active
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-stone-100 text-stone-500"
                            }`}
                          >
                            {mlgSettings?.is_active ? "Active" : "Paused"}
                          </span>
                        </div>
                        <div className="mt-1 max-w-xl text-sm text-[#7a7571]">
                          Any code starting with <span className="font-mono text-[#2a2928]">MLG-</span> and exactly 12
                          characters long is redeemed dynamically at checkout — no need to pre-load codes.
                          {mlgSettings ? (
                            <>
                              {" "}Current offer:{" "}
                              <span className="font-medium text-[#2a2928]">
                                {mlgSettings.discount_type === "percent"
                                  ? `${mlgSettings.discount_value}% off`
                                  : `${formatINR(mlgSettings.discount_value)} off`}
                              </span>
                              {mlgSettings.min_order ? (
                                <> on orders above <span className="font-medium text-[#2a2928]">{formatINR(mlgSettings.min_order)}</span>.</>
                              ) : "."}
                            </>
                          ) : null}
                          <span className="mt-1 block text-xs text-[#a08a7a]">
                            Applies to Hampers &amp; Gifts only — Customised Chocolates are enquiry-only and not eligible.
                          </span>
                        </div>
                      </div>
                    </div>
                    <label className="flex items-center gap-3 self-start md:self-auto">
                      <span className="text-xs uppercase tracking-wider text-[#7a7571]">
                        {mlgSettings?.is_active ? "On" : "Off"}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={!!mlgSettings?.is_active}
                        disabled={!mlgSettings}
                        onClick={() => toggleMlgOffer(!mlgSettings?.is_active)}
                        data-testid="admin-mlg-toggle"
                        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                          mlgSettings?.is_active ? "bg-[#d08c8c]" : "bg-stone-300"
                        } ${!mlgSettings ? "opacity-50" : ""}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                            mlgSettings?.is_active ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </label>
                  </div>
                </div>

                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#7a7571]">
                    <Ticket className="h-4 w-4 text-[#d08c8c]" />
                    Manage discount codes. MyLocalGo codes are pattern-matched (MLG- + 8 chars) — no need to store them here.
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid="admin-add-coupon-btn" onClick={openNewCoupon} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
                      <Plus className="mr-2 h-4 w-4" /> Add coupon
                    </Button>
                  </div>
                </div>
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  {coupons.length === 0 ? (
                    <div className="py-10 text-center text-[#7a7571]" data-testid="admin-no-coupons">No coupons yet.</div>
                  ) : (
                    <div className="space-y-2" data-testid="admin-coupons-list">
                      {coupons.map((c) => (
                        <div key={c.code} data-testid={`admin-coupon-${c.code}`} className="grid grid-cols-1 gap-3 rounded-2xl border border-[#e8e2da] p-4 md:grid-cols-6">
                          <div>
                            <div className="font-serif text-lg tracking-wide">{c.code}</div>
                            <div className="text-xs text-[#7a7571]">{c.source}{c.is_active ? "" : " · inactive"}</div>
                          </div>
                          <div className="text-sm">
                            <div className="text-[#2a2928]">
                              {c.discount_type === "percent" ? `${c.discount_value}% off` : `${formatINR(c.discount_value)} off`}
                            </div>
                            <div className="text-xs text-[#7a7571]">Min order: {c.min_order ? formatINR(c.min_order) : "none"}</div>
                          </div>
                          <div className="text-sm">
                            <div className="text-xs uppercase tracking-wider text-[#7a7571]">Uses</div>
                            <div>{c.uses_count} / {c.usage_limit || "∞"}</div>
                          </div>
                          <div className="text-sm md:col-span-2">
                            <div className="text-xs uppercase tracking-wider text-[#7a7571]">Notes</div>
                            <div className="line-clamp-2 text-[#2a2928]">{c.notes || "—"}</div>
                          </div>
                          <div className="flex items-start justify-end gap-1">
                            <button data-testid={`admin-edit-coupon-${c.code}`} onClick={() => openEditCoupon(c)} className="rounded-full p-2 hover:bg-[#f4ede6]">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button data-testid={`admin-delete-coupon-${c.code}`} onClick={() => deleteCoupon(c.code)} className="rounded-full p-2 hover:bg-red-50">
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* PINCODES */}
              <TabsContent value="pincodes" className="mt-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-[#7a7571]">
                    <MapPin className="h-4 w-4 text-[#d08c8c]" />
                    Balloon hampers deliver only to pincodes on this list. Chocolates ship all-India.
                  </div>
                  <Button data-testid="admin-add-pincode-btn" onClick={openNewPin} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">
                    <Plus className="mr-2 h-4 w-4" /> Add pincode
                  </Button>
                </div>
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  {pincodes.length === 0 ? (
                    <div className="py-10 text-center text-[#7a7571]" data-testid="admin-no-pincodes">No pincodes yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-pincodes-list">
                      {pincodes.map((p) => (
                        <div key={p.pincode} data-testid={`admin-pincode-${p.pincode}`} className="rounded-2xl border border-[#e8e2da] p-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-serif text-lg">{p.pincode}</div>
                              <div className="text-xs text-[#7a7571]">{p.city || "—"}</div>
                              {!p.is_active && <span className="mt-1 inline-block rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-700">inactive</span>}
                            </div>
                            <div className="flex gap-1">
                              <button data-testid={`admin-edit-pincode-${p.pincode}`} onClick={() => openEditPin(p)} className="rounded-full p-1.5 hover:bg-[#f4ede6]">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button data-testid={`admin-delete-pincode-${p.pincode}`} onClick={() => deletePin(p.pincode)} className="rounded-full p-1.5 hover:bg-red-50">
                                <Trash2 className="h-3.5 w-3.5 text-red-600" />
                              </button>
                            </div>
                          </div>
                          {p.notes && <div className="mt-2 text-xs text-[#7a7571]">{p.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              {/* ENQUIRIES */}
              <TabsContent value="enquiries" className="mt-6">
                <div className="mb-4 flex items-center gap-2 text-sm text-[#7a7571]">
                  <Mail className="h-4 w-4 text-[#d08c8c]" />
                  Customised-chocolate enquiries from shoppers. Contact them within 24 hours to convert.
                </div>
                <div className="rounded-3xl border border-[#e8e2da] bg-white p-6">
                  {enquiries.length === 0 ? (
                    <div className="py-10 text-center text-[#7a7571]" data-testid="admin-no-enquiries">
                      No enquiries yet.
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="admin-enquiries-list">
                      {enquiries.map((e) => (
                        <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="grid grid-cols-1 gap-3 rounded-2xl border border-[#e8e2da] p-4 md:grid-cols-6">
                          <div className="md:col-span-2">
                            <div className="font-serif text-lg">{e.name}</div>
                            <div className="text-xs text-[#7a7571]">{e.email}</div>
                            <div className="text-xs text-[#7a7571]">{e.phone}</div>
                            <div className="mt-1 text-xs text-[#7a7571]">{format(new Date(e.created_at), "PPP p")}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wider text-[#7a7571]">Product</div>
                            <div className="text-sm">{e.chocolate_name || "—"}</div>
                            <div className="text-xs text-[#7a7571]">{e.occasion}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wider text-[#7a7571]">Quantity</div>
                            <div className="text-lg font-serif">{e.quantity}</div>
                            {e.price_per_piece ? (
                              <div
                                data-testid={`admin-enquiry-price-${e.id}`}
                                className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[#f4ede6] px-2 py-0.5 text-[11px] font-medium text-[#8a5a5a]"
                                title={`₹${e.price_per_piece} per chocolate · total ≈ ₹${e.price_per_piece * e.quantity}`}
                              >
                                ₹{e.price_per_piece}/pc
                                <span className="text-[#b87a7a]">
                                  · ≈ ₹{e.price_per_piece * e.quantity}
                                </span>
                              </div>
                            ) : null}
                            <div className="text-xs text-[#7a7571]">
                              {e.required_date ? `by ${format(new Date(e.required_date), "PPP")}` : "no date"}
                            </div>
                          </div>
                          <div className="md:col-span-1">
                            <div className="text-xs uppercase tracking-wider text-[#7a7571]">Address / notes</div>
                            <div className="line-clamp-2 text-sm">{e.address || "—"}</div>
                            {e.message && <div className="mt-1 line-clamp-2 text-xs text-[#7a7571]">{e.message}</div>}
                          </div>
                          <div className="flex flex-col items-start gap-2">
                            <Select value={e.status || "new"} onValueChange={(v) => setEnquiryStatus(e.id, v)}>
                              <SelectTrigger className="h-8 w-full rounded-full text-xs" data-testid={`admin-enquiry-status-${e.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ENQ_STATUS.map((s) => (
                                  <SelectItem value={s} key={s}>{s}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <button
                              data-testid={`admin-enquiry-delete-${e.id}`}
                              onClick={() => deleteEnquiry(e.id)}
                              className="text-xs text-red-600 hover:underline"
                            >
                              delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Product Editor Dialog */}
      <Dialog open={openProd} onOpenChange={setOpenProd}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing?.id ? "Edit hamper" : "Add hamper"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Name</Label>
                <Input data-testid="prod-name" className="mt-2 rounded-xl" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="prod-category" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>
                        {c.parent_slug ? `— ${c.name}` : c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={editing.product_type || "hamper"} onValueChange={(v) => setEditing({ ...editing, product_type: v })}>
                  <SelectTrigger data-testid="prod-type" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hamper">Balloon Hamper (pincode-restricted)</SelectItem>
                    <SelectItem value="chocolate">Customised Chocolate (enquiry-only, min 100)</SelectItem>
                    <SelectItem value="gift">Occasional Gift (direct purchase)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Min order quantity</Label>
                <Input data-testid="prod-min-qty" type="number" min={1} className="mt-2 rounded-xl" value={editing.min_order_quantity || 1}
                  onChange={(e) => setEditing({ ...editing, min_order_quantity: Math.max(1, parseInt(e.target.value || "1", 10)) })} />
              </div>
              <div>
                <Label>Price (INR)</Label>
                <Input data-testid="prod-price" type="number" className="mt-2 rounded-xl" value={editing.price}
                  onChange={(e) => setEditing({ ...editing, price: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div className="md:col-span-2">
                <Label>Short description</Label>
                <Input data-testid="prod-short-desc" className="mt-2 rounded-xl" value={editing.short_description}
                  onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Full description</Label>
                <Textarea data-testid="prod-desc" rows={4} className="mt-2 rounded-xl" value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Image</Label>
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={editing.image_base64 || editing.image_url || "https://via.placeholder.com/120x150?text=Preview"}
                    alt="Preview"
                    className="h-24 w-24 rounded-xl border border-[#e8e2da] object-cover"
                  />
                  <div className="flex-1 space-y-2">
                    <Input data-testid="prod-image-url" placeholder="Paste an image URL" className="rounded-xl" value={editing.image_url}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value, image_base64: null })} />
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#e8e2da] px-4 py-2 text-sm hover:border-[#d08c8c]">
                      <Upload className="h-4 w-4" />
                      Upload image
                      <input data-testid="prod-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e, setEditing)} />
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input id="feat" data-testid="prod-featured" type="checkbox" checked={!!editing.featured}
                  onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                <Label htmlFor="feat">Featured on homepage</Label>
              </div>
              <div className="flex items-center gap-2">
                <input id="stock" data-testid="prod-instock" type="checkbox" checked={!!editing.in_stock}
                  onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} />
                <Label htmlFor="stock">In stock</Label>
              </div>

              {/* Toys manager (only for hampers) */}
              {(editing.product_type || "hamper") === "hamper" && (
                <div className="md:col-span-2 mt-2 rounded-2xl border border-[#e8e2da] bg-[#faf9f6] p-4" data-testid="prod-toys-manager">
                  <div className="mb-3 flex items-center justify-between">
                    <Label>Toys placed inside the balloon</Label>
                    <button
                      type="button"
                      data-testid="prod-add-toy-btn"
                      onClick={() => setEditing({
                        ...editing,
                        toys: [...(editing.toys || []), { id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: "", image_url: "", image_base64: null, in_stock: true }],
                      })}
                      className="inline-flex items-center gap-1 rounded-full border border-[#e8e2da] bg-white px-3 py-1 text-xs hover:border-[#d08c8c]"
                    >
                      <Plus className="h-3 w-3" /> Add toy
                    </button>
                  </div>
                  {(editing.toys || []).length === 0 ? (
                    <div className="rounded-xl border border-dashed border-[#e8e2da] p-4 text-center text-xs text-[#7a7571]">
                      No toys yet. Add plush, dolls, teddies etc. for shoppers to pick from.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {(editing.toys || []).map((t, idx) => (
                        <div key={t.id || idx} data-testid={`prod-toy-row-${idx}`} className="flex items-center gap-3 rounded-xl bg-white p-2">
                          <img
                            src={t.image_base64 || t.image_url || "https://via.placeholder.com/60?text=Toy"}
                            alt=""
                            className="h-14 w-14 rounded-lg border border-[#e8e2da] object-cover"
                          />
                          <div className="flex-1 space-y-1">
                            <Input
                              data-testid={`prod-toy-name-${idx}`}
                              placeholder="Toy name (e.g., Teddy bear)"
                              value={t.name}
                              onChange={(e) => {
                                const next = [...editing.toys];
                                next[idx] = { ...next[idx], name: e.target.value };
                                setEditing({ ...editing, toys: next });
                              }}
                              className="h-8 rounded-lg text-sm"
                            />
                            <div className="flex items-center gap-2">
                              <Input
                                data-testid={`prod-toy-url-${idx}`}
                                placeholder="Image URL"
                                value={t.image_url}
                                onChange={(e) => {
                                  const next = [...editing.toys];
                                  next[idx] = { ...next[idx], image_url: e.target.value, image_base64: null };
                                  setEditing({ ...editing, toys: next });
                                }}
                                className="h-8 flex-1 rounded-lg text-xs"
                              />
                              <label className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-[#e8e2da] px-2 py-1 text-[10px] hover:border-[#d08c8c]">
                                <Upload className="h-3 w-3" /> Upload
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  data-testid={`prod-toy-upload-${idx}`}
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 15 * 1024 * 1024) return toast.error("Image must be under 15MB");
                                    const uploaded = await uploadImageToStorage(file);
                                    if (uploaded && !uploaded.fatal) {
                                      const next = [...editing.toys];
                                      next[idx] = { ...next[idx], image_url: uploaded.url, image_base64: null };
                                      setEditing({ ...editing, toys: next });
                                    } else if (!uploaded) {
                                      // Legacy base64 fallback (object storage disabled)
                                      const reader = new FileReader();
                                      reader.onload = () => {
                                        const next = [...editing.toys];
                                        next[idx] = { ...next[idx], image_base64: reader.result, image_url: "" };
                                        setEditing({ ...editing, toys: next });
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                    e.target.value = "";
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                          <button
                            type="button"
                            data-testid={`prod-toy-delete-${idx}`}
                            onClick={() => setEditing({ ...editing, toys: editing.toys.filter((_, i) => i !== idx) })}
                            className="rounded-full p-1.5 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenProd(false)}>Cancel</Button>
            <Button data-testid="prod-save" onClick={saveProduct} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add-on (Gift) Editor Dialog */}
      <Dialog open={openAddon} onOpenChange={setOpenAddon}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="admin-addon-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingAddon?.id ? "Edit gift" : "Add gift"}
            </DialogTitle>
          </DialogHeader>
          {editingAddon && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="addon-name" className="mt-2 rounded-xl" value={editingAddon.name}
                  onChange={(e) => setEditingAddon({ ...editingAddon, name: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="addon-desc" rows={3} className="mt-2 rounded-xl" value={editingAddon.description}
                  onChange={(e) => setEditingAddon({ ...editingAddon, description: e.target.value })} />
              </div>
              <div>
                <Label>Price (INR)</Label>
                <Input data-testid="addon-price" type="number" className="mt-2 rounded-xl" value={editingAddon.price}
                  onChange={(e) => setEditingAddon({ ...editingAddon, price: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div>
                <Label>Image</Label>
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={editingAddon.image_base64 || editingAddon.image_url || "https://via.placeholder.com/120x120?text=Preview"}
                    alt="Preview"
                    className="h-20 w-20 rounded-xl border border-[#e8e2da] object-cover"
                  />
                  <div className="flex-1 space-y-2">
                    <Input data-testid="addon-image-url" placeholder="Paste an image URL" className="rounded-xl" value={editingAddon.image_url}
                      onChange={(e) => setEditingAddon({ ...editingAddon, image_url: e.target.value, image_base64: null })} />
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#e8e2da] px-4 py-2 text-sm hover:border-[#d08c8c]">
                      <Upload className="h-4 w-4" />
                      Upload image
                      <input data-testid="addon-image-upload" type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e, setEditingAddon)} />
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input id="astock" data-testid="addon-instock" type="checkbox" checked={!!editingAddon.in_stock}
                  onChange={(e) => setEditingAddon({ ...editingAddon, in_stock: e.target.checked })} />
                <Label htmlFor="astock">In stock (visible to shoppers)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenAddon(false)}>Cancel</Button>
            <Button data-testid="addon-save" onClick={saveAddon} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Editor Dialog */}
      <Dialog open={openCat} onOpenChange={setOpenCat}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="admin-category-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingCat?._isNew ? "Add category" : "Edit category"}
            </DialogTitle>
          </DialogHeader>
          {editingCat && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Name</Label>
                <Input data-testid="cat-name" className="mt-2 rounded-xl" value={editingCat.name}
                  onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value, ...(editingCat._isNew && !editingCat.slug ? { slug: slugify(e.target.value) } : {}) })} />
              </div>
              <div>
                <Label>Slug {editingCat._isNew ? "(auto if blank)" : "(read-only after creation)"}</Label>
                <Input data-testid="cat-slug" disabled={!editingCat._isNew} className="mt-2 rounded-xl" value={editingCat.slug || ""}
                  onChange={(e) => setEditingCat({ ...editingCat, slug: slugify(e.target.value) })} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="cat-desc" rows={2} className="mt-2 rounded-xl" value={editingCat.description || ""}
                  onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })} />
              </div>
              <div>
                <Label>Parent (leave blank for top-level)</Label>
                <Select
                  value={editingCat.parent_slug || "__none"}
                  onValueChange={(v) => setEditingCat({ ...editingCat, parent_slug: v === "__none" ? null : v })}
                >
                  <SelectTrigger data-testid="cat-parent" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None (top-level)</SelectItem>
                    {topCats.filter((c) => c.slug !== editingCat.slug).map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Order (lower shows first)</Label>
                <Input data-testid="cat-order" type="number" className="mt-2 rounded-xl" value={editingCat.order || 0}
                  onChange={(e) => setEditingCat({ ...editingCat, order: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCat(false)}>Cancel</Button>
            <Button data-testid="cat-save" onClick={saveCat} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon Editor Dialog */}
      <Dialog open={openCoupon} onOpenChange={setOpenCoupon}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="admin-coupon-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingCoupon?._isNew ? "Add coupon" : "Edit coupon"}
            </DialogTitle>
          </DialogHeader>
          {editingCoupon && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Code</Label>
                <Input data-testid="coupon-code" disabled={!editingCoupon._isNew} className="mt-2 rounded-xl uppercase"
                  value={editingCoupon.code}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, code: e.target.value.toUpperCase() })} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={editingCoupon.discount_type} onValueChange={(v) => setEditingCoupon({ ...editingCoupon, discount_type: v })}>
                  <SelectTrigger data-testid="coupon-type" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent off</SelectItem>
                    <SelectItem value="fixed">Flat ₹ off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value ({editingCoupon.discount_type === "percent" ? "%" : "₹"})</Label>
                <Input data-testid="coupon-value" type="number" className="mt-2 rounded-xl" value={editingCoupon.discount_value}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, discount_value: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div>
                <Label>Min order (₹)</Label>
                <Input data-testid="coupon-min-order" type="number" className="mt-2 rounded-xl" value={editingCoupon.min_order || 0}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, min_order: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div>
                <Label>Usage limit (0 = unlimited)</Label>
                <Input data-testid="coupon-usage-limit" type="number" className="mt-2 rounded-xl" value={editingCoupon.usage_limit || 0}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, usage_limit: parseInt(e.target.value || "0", 10) })} />
              </div>
              <div>
                <Label>Source</Label>
                <Select value={editingCoupon.source} onValueChange={(v) => setEditingCoupon({ ...editingCoupon, source: v })}>
                  <SelectTrigger data-testid="coupon-source" className="mt-2 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="mylocalgo">MyLocalGo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input id="cactive" data-testid="coupon-active" type="checkbox" checked={!!editingCoupon.is_active}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, is_active: e.target.checked })} />
                <Label htmlFor="cactive">Active</Label>
              </div>
              <div className="md:col-span-2">
                <Label>Notes (internal)</Label>
                <Textarea data-testid="coupon-notes" rows={2} className="mt-2 rounded-xl" value={editingCoupon.notes || ""}
                  onChange={(e) => setEditingCoupon({ ...editingCoupon, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCoupon(false)}>Cancel</Button>
            <Button data-testid="coupon-save" onClick={saveCoupon} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pincode Editor Dialog */}
      <Dialog open={openPin} onOpenChange={setOpenPin}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-pincode-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editingPin?._isNew ? "Add pincode" : "Edit pincode"}
            </DialogTitle>
          </DialogHeader>
          {editingPin && (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Pincode (6 digits)</Label>
                <Input data-testid="pin-code" disabled={!editingPin._isNew} className="mt-2 rounded-xl" maxLength={6}
                  value={editingPin.pincode}
                  onChange={(e) => setEditingPin({ ...editingPin, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })} />
              </div>
              <div>
                <Label>City / area</Label>
                <Input data-testid="pin-city" className="mt-2 rounded-xl" value={editingPin.city}
                  onChange={(e) => setEditingPin({ ...editingPin, city: e.target.value })} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea data-testid="pin-notes" rows={2} className="mt-2 rounded-xl" value={editingPin.notes || ""}
                  onChange={(e) => setEditingPin({ ...editingPin, notes: e.target.value })} />
              </div>
              <div className="flex items-center gap-2">
                <input id="pactive" data-testid="pin-active" type="checkbox" checked={!!editingPin.is_active}
                  onChange={(e) => setEditingPin({ ...editingPin, is_active: e.target.checked })} />
                <Label htmlFor="pactive">Active (deliverable)</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenPin(false)}>Cancel</Button>
            <Button data-testid="pin-save" onClick={savePin} className="rounded-full bg-[#d08c8c] text-white hover:bg-[#b87a7a]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-3xl border border-[#e8e2da] bg-white p-6" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-[#faf1ee] p-2.5">
          <Icon className="h-5 w-5 text-[#d08c8c]" />
        </div>
        <div className="text-xs uppercase tracking-[0.18em] text-[#7a7571]">{label}</div>
      </div>
      <div className="mt-4 font-serif text-3xl text-[#2a2928]">{value}</div>
    </div>
  );
}

